import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';
import DebtPayment from './DebtPayment';
import DebtPaymentHistory from './DebtPaymentHistory';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/debt-details.css';

const DebtDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [debt, setDebt] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    loadDebtDetails();
  }, [id]);

  const loadDebtDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/debts.php/${id}`);
      setDebt(response.data);
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Failed to load debt details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentComplete = () => {
    setShowPaymentModal(false);
    loadDebtDetails();
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'Active',
      overdue: 'Overdue',
      settled: 'Settled',
      partial: 'Partial'
    };
    return <span className={`status-badge ${status}`}>{badges[status]}</span>;
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="debt-details">
      <div className="details-header">
        <Button variant="secondary" onClick={() => navigate('/debts')}>
          ← Back to Debts
        </Button>
        <h1>Debt Details</h1>
      </div>

      <div className="details-grid">
        <Card className="debt-info-card">
          <div className="card-header">
            <h3>Debt Information</h3>
            {debt.status !== 'settled' && (
              <Button 
                variant="primary" 
                onClick={() => setShowPaymentModal(true)}
                icon="💰"
              >
                Record Payment
              </Button>
            )}
          </div>

          <div className="info-sections">
            <div className="info-section">
              <h4>Customer Details</h4>
              <div className="info-row">
                <label>Name:</label>
                <span>{debt.first_name} {debt.last_name}</span>
              </div>
              <div className="info-row">
                <label>Phone:</label>
                <span>{debt.phone}</span>
              </div>
              <div className="info-row">
                <label>Email:</label>
                <span>{debt.email || '-'}</span>
              </div>
            </div>

            <div className="info-section">
              <h4>Sale Details</h4>
              <div className="info-row">
                <label>Invoice:</label>
                <span>{debt.invoice_number}</span>
              </div>
              <div className="info-row">
                <label>Sale Date:</label>
                <span>{formatDate(debt.sale_date)}</span>
              </div>
              <div className="info-row">
                <label>Cashier:</label>
                <span>{debt.cashier_first} {debt.cashier_last}</span>
              </div>
            </div>

            <div className="info-section highlight">
              <h4>Debt Summary</h4>
              <div className="info-row">
                <label>Status:</label>
                <span>{getStatusBadge(debt.status)}</span>
              </div>
              <div className="info-row">
                <label>Original Amount:</label>
                <span>{formatCurrency(debt.original_amount)}</span>
              </div>
              <div className="info-row">
                <label>Paid Amount:</label>
                <span>{formatCurrency(debt.original_amount - debt.remaining_amount)}</span>
              </div>
              <div className="info-row total">
                <label>Remaining Balance:</label>
                <span className="balance">{formatCurrency(debt.remaining_amount)}</span>
              </div>
              <div className="info-row">
                <label>Due Date:</label>
                <span className={debt.status === 'overdue' ? 'overdue' : ''}>
                  {formatDate(debt.due_date)}
                </span>
              </div>
            </div>
          </div>
        </Card>

        <Card className="items-card">
          <h3>Sale Items</h3>
          <table className="items-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {debt.items?.map((item, index) => (
                <tr key={index}>
                  <td>{item.product_name}</td>
                  <td>{item.quantity}</td>
                  <td>{formatCurrency(item.unit_price)}</td>
                  <td>{formatCurrency(item.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card className="payments-card">
          <h3>Payment History</h3>
          <DebtPaymentHistory payments={payments} />
        </Card>
      </div>

      {showPaymentModal && (
        <DebtPayment
          debt={debt}
          onClose={() => setShowPaymentModal(false)}
          onComplete={handlePaymentComplete}
        />
      )}
    </div>
  );
};

export default DebtDetails;